import type {
  SafetyDevice,
  SafetyDeviceTest,
  SafetyDeviceRepository,
  TechnicalAssetDocument,
  SafetyDeviceStatus
} from "./equipment-domain.js";
import { PermissionCode } from "../domain/identity/access-types.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { AuthorizationService } from "../permissions/authorization.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";
import { AuditAction } from "../audit/audit-log.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";
import type { SafetyDeviceId, SafetyDeviceTestId, ClientId, EquipmentId, AreaId } from "../shared/kernel/identifiers.js";
import {
  assertTechnicalAssetHierarchy,
  belongsToProject,
  requireAssetClientId,
  type TechnicalAssetHierarchyLookup
} from "./technical-asset-hierarchy.js";

export interface SafetyDeviceListFilters {
  protectedEquipmentId?: string;
  areaId?: string;
  status?: string;
  projectId?: string;
}

export class SafetyDeviceService {
  constructor(
    private readonly repo: SafetyDeviceRepository,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder
  ) {}

  private hierarchy: TechnicalAssetHierarchyLookup | null = null;
  private authorizedClientIdsResolver: ((context: RequestContext) => Promise<readonly ClientId[]>) | null = null;

  /** Projeto, Área e Equipamento protegido conferidos contra o Cliente do dispositivo. */
  public attachHierarchyLookup(lookup: TechnicalAssetHierarchyLookup): void {
    this.hierarchy = lookup;
  }

  /**
   * "Todos os Clientes" (contexto sem Cliente) é a união dos Clientes
   * AUTORIZADOS à sessão — nunca uma consulta sem filtro de tenant.
   */
  public attachAuthorizedClientIdsResolver(resolver: (context: RequestContext) => Promise<readonly ClientId[]>): void {
    this.authorizedClientIdsResolver = resolver;
  }

  private async scopeClientIds(context: RequestContext): Promise<ClientId[]> {
    if (context.activeClientId) return [context.activeClientId];
    if (!this.authorizedClientIdsResolver) return [];
    return [...new Set((await this.authorizedClientIdsResolver(context)).map(id => String(id)))] as ClientId[];
  }

  /** Projeto de cada equipamento protegido — para filtrar cadastros antigos, sem `projectId` próprio. */
  private async equipmentProjects(devices: readonly SafetyDevice[]): Promise<Map<string, string | null>> {
    const projects = new Map<string, string | null>();
    if (!this.hierarchy) return projects;
    for (const dev of devices) {
      if (!dev.protectedEquipmentId || projects.has(dev.protectedEquipmentId)) continue;
      const equipment = await this.hierarchy.getEquipment(dev.clientId, dev.protectedEquipmentId);
      projects.set(dev.protectedEquipmentId, equipment?.projectId ?? null);
    }
    return projects;
  }

  public async getSafetyDevice(context: RequestContext, id: string): Promise<SafetyDevice | null> {
    this.auth.require(context, PermissionCode.SafetyDeviceRead);
    const dev = await this.repo.getSafetyDevice(context.activeClientId, id as SafetyDeviceId);
    if (!dev) return null;

    // IDOR Tenant Isolation Protection
    if (dev.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Cross-tenant access denied", {
        safeMessage: "Access denied to safety device from another client"
      });
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "SafetyDevice",
        entityId: dev.id,
        previousState: null,
        newState: null,
        metadata: { action: "SAFETY_DEVICE_VIEWED", tag: dev.tag }
      }).catch(() => {});
    }

    return dev;
  }

  public async listSafetyDevices(context: RequestContext, filters?: SafetyDeviceListFilters): Promise<SafetyDevice[]> {
    this.auth.require(context, PermissionCode.SafetyDeviceRead);
    const { projectId, ...repoFilters } = filters ?? {};
    const clientIds = await this.scopeClientIds(context);
    const list = (await Promise.all(clientIds.map(clientId => this.repo.listSafetyDevices(clientId, repoFilters)))).flat();
    if (!projectId) return list;
    const equipmentProjects = await this.equipmentProjects(list);
    return list.filter(dev =>
      belongsToProject({ projectId: dev.projectId ?? null, equipmentId: dev.protectedEquipmentId }, projectId, id => equipmentProjects.get(id) ?? null)
    );
  }

  public async createSafetyDevice(context: RequestContext, data: Partial<SafetyDevice>): Promise<SafetyDevice> {
    this.auth.require(context, PermissionCode.SafetyDeviceCreate);

    // O Cliente do dispositivo é o do contexto da chamada (o servidor já
    // conferiu o acesso da sessão a ele). Um `clientId` diferente no corpo é
    // tentativa de gravar em outro tenant.
    const clientId = requireAssetClientId(context.activeClientId);
    if (data.clientId && String(data.clientId) !== clientId) {
      throw new AppError(ErrorCode.Authorization, "Tenant isolation violation", {
        safeMessage: "O Cliente do cadastro não corresponde ao Cliente selecionado."
      });
    }
    await assertTechnicalAssetHierarchy(this.hierarchy, {
      clientId,
      projectId: data.projectId ?? null,
      areaId: data.areaId ?? null,
      equipmentId: data.protectedEquipmentId ?? null
    });

    const tag = (data.tag || "").trim().toUpperCase();
    if (!tag) {
      throw new AppError(ErrorCode.Validation, "TAG do dispositivo de segurança é obrigatório", {
        safeMessage: "TAG é obrigatório."
      });
    }

    // Check unique tag for this client
    const existingList = await this.repo.listSafetyDevices(clientId);
    const duplicate = existingList.find(d => d.tag.trim().toUpperCase() === tag);
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, `TAG '${tag}' já está cadastrado para este cliente`, {
        safeMessage: `TAG '${tag}' já existe neste cliente.`
      });
    }

    const now = new Date().toISOString();
    const id = (data.id || `SD-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase()) as SafetyDeviceId;

    const device: SafetyDevice = {
      id,
      clientId,
      tag,
      name: data.name || tag,
      deviceType: data.deviceType || 'PSV',
      description: data.description || null,
      manufacturer: data.manufacturer || null,
      model: data.model || null,
      serialNumber: data.serialNumber || null,
      projectId: data.projectId || null,
      protectedEquipmentId: (data.protectedEquipmentId as EquipmentId) || null,
      protectedSystemId: data.protectedSystemId || null,
      protectedLineId: data.protectedLineId || null,
      setPressure: data.setPressure ?? null,
      setPressureUnit: data.setPressureUnit || 'kgf/cm²',
      backPressure: data.backPressure ?? null,
      designPressure: data.designPressure ?? null,
      overpressureAllowance: data.overpressureAllowance ?? null,
      inletSize: data.inletSize || null,
      outletSize: data.outletSize || null,
      orificeDesignation: data.orificeDesignation || null,
      connectionType: data.connectionType || 'Flangeada',
      bodyMaterial: data.bodyMaterial || null,
      trimMaterial: data.trimMaterial || null,
      springMaterial: data.springMaterial || null,
      service: data.service || null,
      fluid: data.fluid || null,
      areaId: (data.areaId as AreaId) || null,
      location: data.location || null,
      status: (data.status as SafetyDeviceStatus) || 'ACTIVE',
      notes: data.notes || null,
      lastTestDate: data.lastTestDate || null,
      nextTestDate: data.nextTestDate || null,
      certificateNumber: data.certificateNumber || null,
      testPressure: data.testPressure ?? null,
      setPressureVerified: data.setPressureVerified ?? null,
      result: data.result || null,
      responsibleProfessionalId: data.responsibleProfessionalId || null,
      certificateDocumentId: data.certificateDocumentId || null,
      version: 1,
      createdAt: now,
      createdBy: context.userId || 'system',
      updatedAt: now,
      updatedBy: context.userId || 'system'
    };

    await this.repo.saveSafetyDevice(device);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "SafetyDevice",
        entityId: device.id,
        previousState: null,
        newState: { tag: device.tag, type: device.deviceType, status: device.status, setPressure: device.setPressure },
        metadata: { action: "SAFETY_DEVICE_CREATED" }
      }).catch(() => {});
    }

    return device;
  }

  public async updateSafetyDevice(context: RequestContext, data: Partial<SafetyDevice> & { id: string }): Promise<SafetyDevice> {
    this.auth.require(context, PermissionCode.SafetyDeviceUpdate);

    const existing = await this.repo.getSafetyDevice(context.activeClientId, data.id as SafetyDeviceId);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Dispositivo de segurança não encontrado", {
        safeMessage: "Dispositivo de segurança não encontrado."
      });
    }

    const tag = (data.tag || existing.tag).trim().toUpperCase();
    if (tag !== existing.tag.trim().toUpperCase()) {
      const existingList = await this.repo.listSafetyDevices(context.activeClientId);
      const duplicate = existingList.find(d => d.tag.trim().toUpperCase() === tag && d.id !== existing.id);
      if (duplicate) {
        throw new AppError(ErrorCode.Conflict, `TAG '${tag}' já está cadastrado para este cliente`, {
          safeMessage: `TAG '${tag}' já existe neste cliente.`
        });
      }
    }

    const now = new Date().toISOString();
    const updated: SafetyDevice = {
      ...existing,
      ...data,
      id: existing.id,
      clientId: existing.clientId,
      tag,
      version: (existing.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId || 'system'
    };

    // O Cliente não muda na edição; o vínculo ALTERADO (Projeto, Área,
    // Equipamento protegido) continua tendo de pertencer a ele. Vínculo
    // intocado de um cadastro antigo não é reavaliado.
    const changed = (a: string | null | undefined, b: string | null | undefined) => (a ?? null) !== (b ?? null);
    const projectChanged = changed(updated.projectId, existing.projectId);
    const equipmentChanged = changed(updated.protectedEquipmentId, existing.protectedEquipmentId);
    await assertTechnicalAssetHierarchy(this.hierarchy, {
      clientId: existing.clientId,
      projectId: projectChanged || equipmentChanged ? updated.projectId ?? null : null,
      areaId: changed(updated.areaId, existing.areaId) ? updated.areaId : null,
      equipmentId: projectChanged || equipmentChanged ? updated.protectedEquipmentId : null
    });

    await this.repo.saveSafetyDevice(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "SafetyDevice",
        entityId: updated.id,
        previousState: { tag: existing.tag, status: existing.status, version: existing.version },
        newState: { tag: updated.tag, status: updated.status, version: updated.version },
        metadata: { action: "SAFETY_DEVICE_UPDATED" }
      }).catch(() => {});
    }

    return updated;
  }

  /**
   * Controlled Deletion of Safety Device:
   * Checks if tests, certificates or documents exist.
   * If history exists -> REJECTS Hard Delete and instructs Inactivation.
   */
  public async deleteSafetyDevice(
    context: RequestContext,
    safetyDeviceId: string,
    reason: string
  ): Promise<{ deleted: boolean; message: string }> {
    this.auth.require(context, PermissionCode.SafetyDeviceDelete);

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a exclusão do dispositivo de segurança."
      });
    }

    const dev = await this.repo.getSafetyDevice(context.activeClientId, safetyDeviceId as SafetyDeviceId);
    if (!dev) {
      throw new AppError(ErrorCode.NotFound, "Dispositivo de segurança não encontrado", {
        safeMessage: "Dispositivo de segurança não encontrado."
      });
    }

    const tests = await this.repo.listTests(context.activeClientId, dev.id);
    const docs = await this.repo.listDocuments(context.activeClientId, dev.id);

    if (tests.length > 0 || docs.length > 0) {
      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Delete,
          entityType: "SafetyDevice",
          entityId: dev.id,
          previousState: { tag: dev.tag, status: dev.status },
          newState: null,
          metadata: {
            action: "SAFETY_DEVICE_DELETE_DENIED_HISTORY_EXISTS",
            testsCount: tests.length,
            documentsCount: docs.length,
            reason
          }
        }).catch(() => {});
      }

      throw new AppError(
        ErrorCode.Conflict,
        "ESTE DISPOSITIVO DE SEGURANÇA POSSUI HISTÓRICO DE TESTES/DOCUMENTAL E NÃO PODE SER EXCLUÍDO DEFINITIVAMENTE.",
        {
          safeMessage: "ESTE DISPOSITIVO DE SEGURANÇA POSSUI HISTÓRICO DE TESTES OU DOCUMENTOS E NÃO PODE SER EXCLUÍDO DEFINITIVAMENTE. Utilize a ação de inativação."
        }
      );
    }

    await this.repo.deleteSafetyDevice(context.activeClientId, dev.id);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "SafetyDevice",
        entityId: dev.id,
        previousState: { tag: dev.tag, status: dev.status },
        newState: null,
        metadata: {
          action: "SAFETY_DEVICE_DELETED",
          deletedBy: context.userId,
          deletionReason: reason
        }
      }).catch(() => {});
    }

    return {
      deleted: true,
      message: `Dispositivo ${dev.tag} excluído definitivamente com sucesso.`
    };
  }

  public async inactivateSafetyDevice(
    context: RequestContext,
    safetyDeviceId: string,
    reason: string
  ): Promise<SafetyDevice> {
    this.auth.require(context, PermissionCode.SafetyDeviceInactivate);

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a inativação."
      });
    }

    const dev = await this.repo.getSafetyDevice(context.activeClientId, safetyDeviceId as SafetyDeviceId);
    if (!dev) {
      throw new AppError(ErrorCode.NotFound, "Dispositivo não encontrado", {
        safeMessage: "Dispositivo não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: SafetyDevice = {
      ...dev,
      status: 'INACTIVE',
      inactivatedAt: now,
      inactivatedBy: context.userId,
      inactivationReason: reason,
      version: (dev.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId
    };

    await this.repo.saveSafetyDevice(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "SafetyDevice",
        entityId: dev.id,
        previousState: { status: dev.status },
        newState: { status: 'INACTIVE' },
        metadata: {
          action: "SAFETY_DEVICE_INACTIVATED",
          inactivatedBy: context.userId,
          inactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  public async reactivateSafetyDevice(
    context: RequestContext,
    safetyDeviceId: string,
    reason: string = "Reativação de cadastro de dispositivo de segurança"
  ): Promise<SafetyDevice> {
    this.auth.require(context, PermissionCode.SafetyDeviceReactivate);

    const dev = await this.repo.getSafetyDevice(context.activeClientId, safetyDeviceId as SafetyDeviceId);
    if (!dev) {
      throw new AppError(ErrorCode.NotFound, "Dispositivo não encontrado", {
        safeMessage: "Dispositivo não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: SafetyDevice = {
      ...dev,
      status: 'ACTIVE',
      inactivatedAt: null,
      inactivatedBy: null,
      inactivationReason: null,
      version: (dev.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId
    };

    await this.repo.saveSafetyDevice(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.StatusChange,
        entityType: "SafetyDevice",
        entityId: dev.id,
        previousState: { status: dev.status },
        newState: { status: 'ACTIVE' },
        metadata: {
          action: "SAFETY_DEVICE_REACTIVATED",
          reactivatedBy: context.userId,
          reactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  // ==========================================
  // TESTS / CERTIFICATIONS (PSV TEST BENCH)
  // ==========================================

  public async listTests(context: RequestContext, safetyDeviceId: string): Promise<SafetyDeviceTest[]> {
    this.auth.require(context, PermissionCode.SafetyDeviceRead);
    return this.repo.listTests(context.activeClientId, safetyDeviceId as SafetyDeviceId);
  }

  public async addTest(
    context: RequestContext,
    data: {
      safetyDeviceId: string;
      date: string;
      certificate: string;
      result: 'CONFORMING' | 'NON_CONFORMING' | 'ADJUSTED';
      setPressureFound?: number | string;
      setPressureAdjusted?: number | string;
      coldDifferentialTestPressure?: number | string;
      seatTightnessTestPressure?: number | string;
      bubbleCount?: number | string;
      testBench?: string;
      responsibleProfessionalId?: string;
      responsibleName?: string;
      nextDueDate: string | null;
      documentId?: string;
      notes?: string;
    }
  ): Promise<SafetyDeviceTest> {
    this.auth.require(context, PermissionCode.SafetyDeviceUpdate);

    const dev = await this.repo.getSafetyDevice(context.activeClientId, data.safetyDeviceId as SafetyDeviceId);
    if (!dev) {
      throw new AppError(ErrorCode.NotFound, "Dispositivo não encontrado", {
        safeMessage: "Dispositivo não encontrado para registrar teste de calibração."
      });
    }

    // Validation: nextDueDate >= date
    if (data.nextDueDate && new Date(data.nextDueDate) < new Date(data.date)) {
      throw new AppError(ErrorCode.Validation, "Data do próximo teste não pode ser anterior à data do teste realizado", {
        safeMessage: "Data de vencimento do teste deve ser maior ou igual à data do teste."
      });
    }

    const now = new Date().toISOString();
    const id = `TEST-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase() as SafetyDeviceTestId;

    const test: SafetyDeviceTest = {
      id,
      clientId: context.activeClientId,
      safetyDeviceId: dev.id,
      date: data.date,
      result: data.result,
      setPressureFound: data.setPressureFound ?? null,
      setPressureAdjusted: data.setPressureAdjusted ?? null,
      coldDifferentialTestPressure: data.coldDifferentialTestPressure ?? null,
      seatTightnessTestPressure: data.seatTightnessTestPressure ?? null,
      bubbleCount: data.bubbleCount ?? null,
      certificate: data.certificate,
      testBench: data.testBench || null,
      responsibleProfessionalId: data.responsibleProfessionalId || null,
      responsibleName: data.responsibleName || null,
      nextDueDate: data.nextDueDate,
      documentId: data.documentId || null,
      notes: data.notes || null,
      createdAt: now,
      createdBy: context.userId
    };

    await this.repo.saveTest(test);

    // Update safety device summary test data
    const isDue = data.nextDueDate ? new Date(data.nextDueDate) < new Date() : false;
    const updatedDev: SafetyDevice = {
      ...dev,
      lastTestDate: data.date,
      nextTestDate: data.nextDueDate,
      certificateNumber: data.certificate,
      setPressureVerified: data.setPressureAdjusted ?? data.setPressureFound ?? dev.setPressureVerified,
      result: data.result,
      responsibleProfessionalId: data.responsibleProfessionalId || dev.responsibleProfessionalId,
      certificateDocumentId: data.documentId || dev.certificateDocumentId,
      status: isDue ? 'TEST_DUE' : (data.result === 'NON_CONFORMING' ? 'OUT_OF_SERVICE' : 'ACTIVE'),
      updatedAt: now,
      updatedBy: context.userId
    };
    await this.repo.saveSafetyDevice(updatedDev);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "SafetyDeviceTest",
        entityId: test.id,
        previousState: null,
        newState: { deviceTag: dev.tag, certificate: test.certificate, result: test.result },
        metadata: { action: "SAFETY_DEVICE_TEST_ADDED", safetyDeviceId: dev.id }
      }).catch(() => {});
    }

    return test;
  }

  public async deleteTest(context: RequestContext, testId: string): Promise<void> {
    this.auth.require(context, PermissionCode.SafetyDeviceUpdate);
    await this.repo.deleteTest(context.activeClientId, testId as SafetyDeviceTestId);
  }

  // ==========================================
  // DOCUMENTS
  // ==========================================

  public async listDocuments(context: RequestContext, safetyDeviceId: string): Promise<TechnicalAssetDocument[]> {
    this.auth.require(context, PermissionCode.SafetyDeviceRead);
    return this.repo.listDocuments(context.activeClientId, safetyDeviceId);
  }

  public async linkDocument(context: RequestContext, doc: TechnicalAssetDocument): Promise<TechnicalAssetDocument> {
    this.auth.require(context, PermissionCode.TechnicalAssetDocumentsManage);
    await this.repo.saveDocument(doc);
    return doc;
  }

  public async unlinkDocument(context: RequestContext, documentId: string): Promise<void> {
    this.auth.require(context, PermissionCode.TechnicalAssetDocumentsManage);
    await this.repo.removeDocument(context.activeClientId, documentId);
  }
}
